/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { getMinutesAndSeconds } from "@packages/media";
import type { HallowElementProps } from "@packages/ui";
import { numericPercentages, Slider, spacing } from "@packages/ui";
import { HallowDataTestId } from "@tools/playwright/global";
import { useEffect, useState } from "react";

import { Time } from "../../primitives/Time.emotion";

const containerStyle: Interpolation = {
  columnGap: spacing.s,
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  placeContent: "flex-start",
  placeItems: "center",
  width: numericPercentages[100],
};
const onCollapsedPlayerStyle: Interpolation = {
  width: "auto",
};
const onExpandedPlayerStyle: Interpolation = {
  flexDirection: "column",
  placeContent: "flex-start",
  placeItems: "center",
  rowGap: spacing.s,
};
const underTimerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  width: numericPercentages[100],
};

export type ScrubberProps = HallowElementProps<"div"> & {
  durationS: number;
  elapsedS: number;
  showTime?: "under" | "beside" | false;
  jumpTo: (newTime: number) => void;
  disabled?: boolean;
};

export const Scrubber = ({
  durationS,
  elapsedS,
  showTime = false,
  jumpTo,
  children,
  disabled = false,
  ...props
}: ScrubberProps) => {
  const [sliderValue, setSliderValue] = useState<number>(
    (elapsedS / durationS) * 100,
  );
  const [inMotion, setInMotion] = useState<boolean>(false);
  const [displayElapsedS, setDisplayElapsedS] = useState<number>(elapsedS);
  const [displayRemainingS, setDisplayRemainingS] = useState<number>(
    durationS - elapsedS,
  );
  const t = useTranslations();

  /** Radix steps the arrow keys by exactly this, so a percentage is one second of the track. */
  const stepPercent = durationS > 0 ? 100 / durationS : 1;
  const [elapsedMin, elapsedSec] = getMinutesAndSeconds({
    time: displayElapsedS,
  });

  useEffect(() => {
    if (!inMotion) {
      setSliderValue((elapsedS / durationS) * 100);
      setDisplayElapsedS(elapsedS);
      setDisplayRemainingS(Math.max(durationS - elapsedS, 0));
    }
  }, [elapsedS, durationS]);

  const handleMove = ([newPercent]: [number]) => {
    setInMotion(true);
    setSliderValue(newPercent);
    setDisplayElapsedS((newPercent / 100) * durationS);
    setDisplayRemainingS(
      Math.max(durationS - (newPercent / 100) * durationS, 0),
    );
  };

  const handleCommit = ([newPercent]: [number]) => {
    setSliderValue(newPercent);
    jumpTo((newPercent / 100) * durationS);
    setInMotion(false);
  };

  return (
    <div
      css={[
        containerStyle,
        showTime === "beside" ? onCollapsedPlayerStyle : null,
        showTime === "under" ? onExpandedPlayerStyle : null,
      ]}
      {...props}
    >
      {showTime === "beside" ? (
        <Time
          timeS={displayElapsedS}
          data-testid={HallowDataTestId.MediaElapsed}
        />
      ) : null}
      <Slider
        disabled={disabled}
        size={showTime === "beside" ? "small" : "large"}
        data-testid={HallowDataTestId.MediaScrubber}
        label={t("accessibility_label_media_scrubber")}
        valueText={t("web_audio_player_time_elapsed", {
          "0": elapsedMin,
          "1": elapsedSec,
        })}
        onValueChange={handleMove}
        onValueCommit={handleCommit}
        value={sliderValue}
        step={stepPercent}
      />
      {showTime === "beside" ? (
        <Time
          timeS={displayRemainingS}
          negative={true}
          data-testid={HallowDataTestId.MediaRemaining}
        />
      ) : null}
      {showTime === "under" ? (
        <div css={underTimerStyle}>
          <Time
            timeS={displayElapsedS}
            data-testid={HallowDataTestId.MediaElapsed}
          />
          {children}
          <Time
            timeS={displayRemainingS}
            negative={true}
            data-testid={HallowDataTestId.MediaRemaining}
          />
        </div>
      ) : null}
    </div>
  );
};
